import type { SharedProps } from '@bonniernews/dn-design-system-web/assets/types/shared-props.ts'

import { formatClassString } from '@bonniernews/dn-design-system-web/helpers/formatClassString.ts'

interface SwitchProps extends SharedProps {
  name: string
  showMeta?: boolean
  metaOn?: string
  metaOff?: string
  stripLabel?: boolean
  checked?: boolean
  required?: boolean
  disabled?: boolean
  validation?: string
  forcePx?: boolean
}

export type SwitchInnerProps = Pick<SwitchProps, 'showMeta' | 'metaOn' | 'metaOff'> & {
  componentClassName?: string
}

/**
 * - GitHub: [BonnierNews/dn-design-system/web/src/components/switch](https://github.com/BonnierNews/dn-design-system/tree/main/web/src/components/switch)
 * - Storybook: [Checkbox](https://designsystem.dn.se/?path=/docs/basic-form-switch--docs)
 *
 * The component will not include styling by itself. Make sure to include the right styles for the component. See example below:
 * `@use '@bonniernews/dn-design-system-web/components/switch/switch.scss`
 */
export const Switch = ({
  name = 'ds-switch',
  checked,
  showMeta = false,
  metaOn = 'På',
  metaOff = 'Av',
  disabled,
  stripLabel,
  forcePx,
  classNames,
  attributes,
}: SwitchProps) => {
  const componentClassName = 'ds-switch'
  const classes = formatClassString([componentClassName, forcePx && 'ds-force-px', classNames])

  return (
    <div className={classes}>
      <input
        type='checkbox'
        id={name}
        name={name}
        defaultChecked={checked}
        disabled={disabled}
        aria-label={name}
        {...attributes}
      />
      {stripLabel ? (
        <div className={`${componentClassName}__inner`}>
          <SwitchInner
            {...{
              showMeta,
              metaOn,
              metaOff,
              componentClassName,
            }}
          />
        </div>
      ) : (
        <label className={`${componentClassName}__inner`} tabIndex={0} htmlFor={name}>
          <SwitchInner
            {...{
              showMeta,
              metaOn,
              metaOff,
              componentClassName,
            }}
          />
        </label>
      )}
    </div>
  )
}

const SwitchInner = ({ showMeta, metaOn, metaOff, componentClassName }: SwitchInnerProps) => {
  return (
    <>
      {showMeta && (
        <div className={`${componentClassName}__meta`}>
          <span className={`${componentClassName}__meta-on`}>{metaOn}</span>
          <span className={`${componentClassName}__meta-off`}>{metaOff}</span>
        </div>
      )}
      <span className={`${componentClassName}__box`}>
        <span className={`${componentClassName}__knob`}></span>
      </span>
    </>
  )
}
